/**
 * モーダルとショップサイトの通信に関わる全てをここで取り扱う。
 */
import { onBeforeUnmount, ref, unref, computed, Ref, watchEffect, watch, reactive } from '@vue/composition-api';
import { createContext, useContext } from '@berlin-front/common/composables/context';
import { ERROR_TYPE } from '@/services/ModalParameterContext';
import { hasProps, indent, isString, pick } from '@berlin-front/common/utils';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { broadcast } from '@/broadcast';
import eqLoose from '@berlin-front/common/eqLoose';
import useClientConnection, { useOrigin } from '@berlin-front/common/composables/useClientConnection';
import { useErrorHandle } from '@composables/useErrorHandle';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { ROUTE_NAME } from '@/router/route-name';
import { commandSchemas } from '@schemas/command-schema';
import { useFraudAlert } from '@berlin-front/common/composables/useFraudAlert';
import type { ServiceDiscriminator } from './service-type-bridge/index';
import { GlobalApiErrorContext } from './GlobalApiErrorContext';
import useRegisteredServices, { ServiceGlobals } from './service-type-bridge';

const dummyServiceGlobals = {
  pubkey: undefined,
  headerShopName: '',
  headerTotalPrice: '',
  headerTotalPriceUnit: '',
  registerFormDefault: undefined,
  userNo: undefined,
  helpComponent: undefined,
  helpVisibility: false,
  showHelp: () => {},
  hideHelp: () => {},
};

let fraudAlertInitializedResolve: () => void = () => {};
const fraudAlertInitializedPromise = new Promise<void>((_resolve) => {
  fraudAlertInitializedResolve = _resolve;
});

export const useBoot = (): Ref<ServiceGlobals> => {
  const clientConnection = useClientConnection();
  const { clearAll } = useContext(GlobalApiErrorContext);
  const { reset } = useContext(ApiCacheContext);
  const router = useRouter();
  /** コンポーネントライフサイクル終了事に呼ばれる関数。後処理が必要な何かを行った時は、ここにその処理を積み込む。 */
  const onUnmounts: (() => void)[] = [];

  const origin = useOrigin();
  const cookieDomain = computed(
    () =>
      origin.value
        .replace(/^https?:\/\//, '.') // プロトコルの削除
        .replace(/:\d+$/, ''), // port番号の削除
  );
  /** 初回のスタート実行が行われたかどうか。 */
  const firstTimeStartedRef = ref(false);
  /** FraudAlertのscriptが読み込まれたかどうかを保持するref */
  const fraudAlertInitializedRef = useFraudAlert(
    firstTimeStartedRef,
    reactive({
      siteId: process.env.VUE_APP_FRAUD_ALERT_SITE_ID,
      cookieDomain,
      fraudAlertEndpoint: process.env.VUE_APP_FRAUD_ALERT_ENDPOINT,
      fraudAlertStaticContents: process.env.VUE_APP_FRAUD_ALERT_STATIC_CONTENTS,
      fraudAlertScriptName: process.env.VUE_APP_FRAUD_ALERT_SCRIPT_NAME,
    }),
  );
  // FraudAlertのスクリプトが実行されたら待機してるstartを実行する。
  watch([fraudAlertInitializedRef] as const, ([initialized]) => {
    if (initialized) {
      fraudAlertInitializedResolve();
    }
  });

  /** 現在実行中のサービス */
  const currentServiceDiscriminator = ref<ServiceDiscriminator>();
  // ホームに遷移するタイミングでサービス終了とし、ApiCacheのresetを行う。
  router.afterEach((to) => {
    if (to.name === ROUTE_NAME.HOME) {
      currentServiceDiscriminator.value = undefined;
      reset();
    }
  });

  const serviceMessageHandlers = useRegisteredServices(currentServiceDiscriminator);

  /**
   * モーダルの実行状態
   *
   * 停止状態からFraudAlertのスクリプトが埋め込まれるまでの間を'starting'としている。
   */
  const modalStateRef = ref<'stopped' | 'starting' | 'running'>('stopped');
  // ホームに遷移するタイミングでサービス終了とし、モーダル状態を'stopped'にする。
  router.afterEach((to) => {
    if (to.name === ROUTE_NAME.HOME) {
      modalStateRef.value = 'stopped';
    }
  });

  /**
   * サービスに紐づかない共通エラーハンドルを行うかどうか。
   */
  const isActiveCommonErrorHandle = computed(() => modalStateRef.value === 'stopped');

  // 実行中のサービスタイプの値を参照する。
  const currentGlobals = computed(
    () =>
      unref(
        serviceMessageHandlers.find((h) => eqLoose(h.serviceDiscriminator, currentServiceDiscriminator.value))
          ?.serviceGlobals?.value,
      )! ?? dummyServiceGlobals,
  );

  // サービスが起動してない、かつどうしてもコールバックを指定できない場合のエラーハンドル。
  // ここではjs error apiしか実行されない。
  useErrorHandle({
    // ここでのハンドリングはサービスが起動してない時のみ有効。
    activeRef: isActiveCommonErrorHandle,
    // エラーコールバックは何も実行しない。
    errorCallback: (param) => {
      console.error('エラーコールバックは呼べません。');
      console.error(param);
    },
  });
  let unmounted = false;

  /**
   * initで正常に登録されたサービスを記録しておく。
   *
   * 特定のサービスに紐づかないエラーが発生した場合に、この登録されたサービスの一番先頭のサービスのエラーコールバックに通知を行います。
   * 純粋な対エラーコールバック対策。
   */
  const registeredValidServices: Exclude<ServiceDiscriminator, undefined>[] = [];

  /**
   * 保留しておいた初期化メッセージイベント。
   */
  const pendingInitMessageEvents: MessageEvent[] = [];

  /**
   * コマンドメッセージハンドラ
   *
   * 会員サイト -> モーダルへのコマンドは全てここでハンドルする。ないだろうけど、追加コマンドがある場合はここに追加する。
   */
  const commandMessageEventListener = (ev: MessageEvent) => {
    const { data } = ev;
    try {
      {
        const parseResult = commandSchemas.INIT.safeParse(data);
        if (parseResult.success) {
          if (modalStateRef.value !== 'stopped') {
            // 停止時以外は初期化メッセージを保留
            pendingInitMessageEvents.push(ev);
            return;
          }
          // 不正なサービスタイプがないかのスキーマチェック。その他フォーマットチェックはservice_type modal_modeのチェック、配列データかどうかのチェクになる。
          const invalidServices: { service_type: string; modal_mode?: string }[] = [];
          parseResult.data.payload.services
            .flatMap((service) => {
              // サービスを処理できるハンドラを探す。
              const handler = serviceMessageHandlers.find((h) => h.check(service));
              if (handler) {
                return [[service, handler] as const];
              }
              // ハンドラがない場合、不正なサービスとしてエラー扱いにする。
              invalidServices.push(pick(service, 'service_type', 'modal_mode'));
              return [];
            })
            .forEach(([service, handler]) => {
              // 処理できるものだけ処理する。
              handler.init(service);

              const desc = pick(service, 'service_type', 'modal_mode');

              if (
                !registeredValidServices.some(
                  (v) => (v.modal_mode ?? '01') === (desc.modal_mode ?? '01') && v.service_type === desc.service_type,
                )
              ) {
                registeredValidServices.push(desc);
              }
            });

          // 不正なサービスが登録されてる場合、エラー通知する。
          if (invalidServices.length > 0) {
            throw new Error(
              [
                '不正なサービスで初期化を行いました:',
                indent(invalidServices.map((s) => JSON.stringify(s)).join('\n')),
              ].join('\n'),
            );
          }
          return;
        }
      }
      {
        const parseResult = commandSchemas.START.safeParse(data);
        // すでにスタート済みの場合はリクエストを捨てる。
        if (modalStateRef.value !== 'stopped') return;
        if (parseResult.success) {
          // エラーのクリア
          clearAll();
          // fraudalert開始する。
          firstTimeStartedRef.value = true;
          // 一致するサービスがあればスタートする。
          const handler = serviceMessageHandlers.find((h) => h.check(parseResult.data.payload));
          if (handler) {
            modalStateRef.value = 'starting';
            void (async () => {
              await fraudAlertInitializedPromise;
              modalStateRef.value = 'running';
              handler.start(parseResult.data.payload);
            })();
          } else {
            throw new Error(`不正なサービスで起動を行いました: ${JSON.stringify(parseResult.data.payload)}`);
          }
        }
      }
    } catch (e) {
      // サービスタイプに関わらないエラーの捕捉
      // ただしinitもstartも、同期的に実行されて、不正でもエラーを直接throwしないため、不正なサービスで実行しようとした時しか
      // ここにはきません。
      broadcast.send({
        type: 'JS_ERROR',
        payload: {
          ...ERROR_TYPE.REQUEST_EXCEPTION,
          ...(hasProps(e, 'name', isString) ? { name: e.name } : {}),
          ...(hasProps(e, 'stack', isString) ? { stack: e.stack } : {}),
          ...(hasProps(e, 'message', isString) ? { message: e.message } : {}),
        },
      });
    }
  };
  onUnmounts.push(clientConnection.addCommandMessageEventListener(commandMessageEventListener));

  watchEffect(
    () => {
      if (modalStateRef.value === 'stopped' && !unmounted) {
        // 起動してなければ再送。
        // サービスが終了したらペンディングしてた初期化メッセージをぶん回す。
        pendingInitMessageEvents.splice(0).forEach((ev) => {
          commandMessageEventListener(ev);
        });
      }
    },
    { flush: 'sync' },
  );

  /**
   * 直近1分間に発生した非同期エラー発生時刻。これが10件をオーバーしたら以降、
   * - [x] 1. 同じエラーメッセージ+stack traceの場合、覚えておいて重複して通知にないようにする。
   * - [x] 2. 1分間の間に10回以上js error apiを呼ぼうとした場合、エラーAPIを呼ばないようにする。
   * - [ ] 3. 1分間の間に10回以上js error apiを呼ぼうとした場合、専用のエラー画面へ遷移させる（実質トルミルのような不正なアプリでしか発生しないはず）
   */
  // TODO: 残件あり [issues445 - 謎の/javascript/errorの発火](https://github.com/netprotections/berlin-front/issues/445)
  let asyncErrorOccurredTimes: number[] = [];
  /**
   * 過去1000件の非同期エラー内容。ここに重複するエラー内容がある場合はjsErrorApiに通知を行わない。
   */
  let last1000AsyncErrorMessages: string[] = [];
  /**
   * 非同期例外握り潰しflag
   * trueならjsErrorApiを呼び出さない。
   */
  let ignoreAsyncExceptionFlag = false;
  /**
   * どこでもキャッチされなかった非同期エラーのハンドリング。
   * @param ev
   */
  const onAsyncError = (ev: ErrorEvent) => {
    if (ignoreAsyncExceptionFlag) {
      return;
    }
    const error =
      ev.error instanceof Error
        ? {
            name: ev.error.name,
            message: ev.error.message,
            stack: ev.error.stack ?? '',
          }
        : {
            name: 'ScriptError',
            message: ev.message,
            stack: `ScriptError: ${ev.message}\n    at (${ev.filename}:${ev.lineno}:${ev.colno})`,
          };
    const now = Date.now();
    asyncErrorOccurredTimes = [...asyncErrorOccurredTimes.filter((time) => now - time < 60 * 1000), now];
    const currentMessageJson = JSON.stringify(error);
    if (last1000AsyncErrorMessages.includes(currentMessageJson)) {
      // すでに同じスタックトレースの同じエラーが発生してる場合は握り潰す。
      return;
    }
    // エラーメッセージは記憶するが、
    // 無制限に記憶するとメモリリークするため、1000件を超えるエラーメッセージについては忘れさせる。
    last1000AsyncErrorMessages = [
      ...(last1000AsyncErrorMessages.length >= 1000
        ? last1000AsyncErrorMessages.slice(-999)
        : last1000AsyncErrorMessages),
      currentMessageJson,
    ];

    if (asyncErrorOccurredTimes.length > 10) {
      // １分間に発生したエラーが10件を超えたなら、以降非同期例外を握り潰す。
      ignoreAsyncExceptionFlag = true;
      // TODO: 残件あり [issues445 - 謎の/javascript/errorの発火](https://github.com/netprotections/berlin-front/issues/445)
      // ここで特殊エラー画面に遷移するためのメッセージをブロードキャストする。
    }

    broadcast.send({
      type: 'JS_ERROR',
      payload: {
        // エラーは最初に登録されたサービスのハンドラーで通知してしまう。
        ...registeredValidServices.at(0),
        ...error,
      },
    });
  };

  window.addEventListener('error', onAsyncError);
  onUnmounts.push(() => window.removeEventListener('error', onAsyncError));

  // 後処理
  onBeforeUnmount(() => {
    unmounted = true;
    onUnmounts.splice(0).forEach((v) => v());
  });

  return currentGlobals;
};

/* istanbul ignore next */
export const ModalBridgeContext = createContext<ServiceGlobals>(() => useBoot() as unknown as ServiceGlobals);
